import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatCurrency, formatDateLong } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Receipt, Plus, Search, Pencil, Trash2, ArrowLeft, ChevronLeft, ChevronRight, Send, Copy, Ban, DollarSign, Package, X, AlertTriangle, CreditCard, Download, Loader2, Mail, Tag, FileText } from "lucide-react";
import { toast } from "sonner";
import { usePriceListPricing } from "@/hooks/usePriceListPricing";

const statusColors = {
  Draft: "bg-gray-100 text-gray-700 border-gray-300",
  Sent: "bg-blue-50 text-blue-700 border-blue-200",
  "Partially Paid": "bg-orange-50 text-orange-700 border-orange-200",
  Paid: "bg-green-50 text-green-700 border-green-200",
  Overdue: "bg-red-50 text-red-700 border-red-200",
  Void: "bg-gray-100 text-gray-400 border-gray-200",
};

const PAYMENT_METHODS = ["EFT / Bank Transfer", "Cash", "Credit Card", "Debit Card", "Cheque", "Other"];

function triggerDownload(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  window.URL.revokeObjectURL(url);
}

export default function InvoicesPage() {
  const { id } = useParams();
  if (id) return <InvoiceDetail invoiceId={id} />;
  return <InvoiceList />;
}

function InvoiceList() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [selected, setSelected] = useState([]);
  const [downloading, setDownloading] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  const canCreate = ["admin", "sales_manager"].includes(user?.role);

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/invoices", { params: { search, status: statusFilter, page, limit: 25 } });
      setInvoices(res.data.invoices);
      setTotalPages(res.data.pages);
      setTotal(res.data.total);
      setStats(res.data.stats || {});
      setSelected([]);
    } catch { toast.error("Failed to load invoices"); }
    finally { setLoading(false); }
  }, [search, statusFilter, page]);

  useEffect(() => { fetchInvoices(); }, [fetchInvoices]);

  const handleDelete = async () => {
    try { await api.delete(`/invoices/${deleteId}`); toast.success("Invoice deleted"); setDeleteId(null); fetchInvoices(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed to delete"); }
  };

  const toggleSelect = (id) => setSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  const toggleAll = () => setSelected(selected.length === invoices.length ? [] : invoices.map((inv) => inv.invoice_id));

  const handleBulkDownload = async (ids) => {
    if (ids.length === 0) return;
    setDownloading(true);
    try {
      if (ids.length === 1) {
        const res = await api.get(`/invoices/${ids[0]}/pdf`, { responseType: "blob" });
        const inv = invoices.find((x) => x.invoice_id === ids[0]);
        triggerDownload(new Blob([res.data]), `Invoice_${inv?.invoice_number || "INV"}.pdf`);
        toast.success("Invoice PDF downloaded successfully");
      } else {
        const res = await api.post("/invoices/bulk-pdf", { invoice_ids: ids }, { responseType: "blob" });
        triggerDownload(new Blob([res.data]), `Supply_House_Invoices_${new Date().toISOString().split("T")[0]}.zip`);
        toast.success(`${ids.length} invoice PDFs downloaded successfully`);
      }
      setSelected([]);
    } catch { toast.error("Failed to download PDFs"); }
    finally { setDownloading(false); }
  };

  return (
    <div className="animate-fade-in" data-testid="invoices-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading font-bold text-3xl text-[#1F2937] tracking-tight">Invoices</h1>
          <div className="flex flex-wrap items-center gap-3 mt-1">
            {stats.total_outstanding > 0 && <Badge className="bg-orange-50 text-orange-700 rounded-full text-xs">Outstanding: {formatCurrency(stats.total_outstanding)}</Badge>}
            {stats.overdue_count > 0 && <Badge className="bg-red-50 text-red-700 rounded-full text-xs">{stats.overdue_count} Overdue</Badge>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {selected.length > 0 && (
            <Button data-testid="bulk-download-invoices" onClick={() => handleBulkDownload(selected)} disabled={downloading} variant="outline" className="rounded-sm border-gray-300">
              {downloading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Download className="w-4 h-4 mr-1.5" />}
              {downloading ? "Generating..." : `Download ${selected.length} PDF${selected.length > 1 ? "s" : ""}`}
            </Button>
          )}
          {(search || statusFilter) && total > 0 && selected.length === 0 && (
            <Button data-testid="download-filtered-invoices" variant="outline" disabled={downloading}
              onClick={() => handleBulkDownload(invoices.map((inv) => inv.invoice_id))} className="rounded-sm border-gray-300">
              {downloading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Download className="w-4 h-4 mr-1.5" />}
              Download {invoices.length} Filtered
            </Button>
          )}
          {canCreate && (
            <Button data-testid="create-invoice-button" onClick={() => setDialogOpen(true)} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm">
              <Plus className="w-4 h-4 mr-1.5" /> New Invoice
            </Button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-3 mb-6">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input data-testid="invoice-search-input" placeholder="Search by invoice # or customer..." value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }} className="pl-9 h-9 rounded-sm border-gray-300" />
        </div>
        <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v === "all" ? "" : v); setPage(1); }}>
          <SelectTrigger data-testid="invoice-status-filter" className="w-[160px] h-9 rounded-sm border-gray-300"><SelectValue placeholder="All Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            {["Draft", "Sent", "Partially Paid", "Paid", "Overdue", "Void"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Card className="border border-gray-200 rounded-sm shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="bg-gray-50 border-b border-gray-200">
              <th className="px-3 py-3 w-10"><Checkbox data-testid="select-all-invoices" checked={invoices.length > 0 && selected.length === invoices.length} onCheckedChange={toggleAll} /></th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Invoice #</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Customer</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase hidden md:table-cell">Date</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase hidden lg:table-cell">Due Date</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Total</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase hidden md:table-cell">Paid</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Balance</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Status</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Actions</th>
            </tr></thead>
            <tbody>
              {loading ? (
                [...Array(5)].map((_, i) => <tr key={i} className="border-b border-gray-100"><td colSpan={10} className="px-4 py-4"><div className="h-4 bg-gray-100 rounded animate-pulse" /></td></tr>)
              ) : invoices.length === 0 ? (
                <tr><td colSpan={10} className="text-center py-12 text-gray-400">
                  <Receipt className="w-10 h-10 mx-auto mb-3 text-gray-300" /><p className="font-medium">No invoices found</p>
                </td></tr>
              ) : invoices.map((inv) => (
                <tr key={inv.invoice_id} data-testid={`invoice-row-${inv.invoice_id}`} className="border-b border-gray-100 hover:bg-gray-50 transition-colors cursor-pointer">
                  <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                    <Checkbox checked={selected.includes(inv.invoice_id)} onCheckedChange={() => toggleSelect(inv.invoice_id)} />
                  </td>
                  <td className="px-4 py-3 font-mono text-sm font-medium text-[#E63946]" onClick={() => navigate(`/invoices/${inv.invoice_id}`)}>{inv.invoice_number}</td>
                  <td className="px-4 py-3 font-medium text-[#1F2937] max-w-[200px] truncate" onClick={() => navigate(`/invoices/${inv.invoice_id}`)} title={inv.customer_name}>{inv.customer_name}</td>
                  <td className="px-4 py-3 text-gray-600 hidden md:table-cell" onClick={() => navigate(`/invoices/${inv.invoice_id}`)}>{formatDateLong(inv.created_at)}</td>
                  <td className="px-4 py-3 text-gray-600 hidden lg:table-cell" onClick={() => navigate(`/invoices/${inv.invoice_id}`)}>{inv.due_date || "-"}</td>
                  <td className="px-4 py-3 text-right font-medium" onClick={() => navigate(`/invoices/${inv.invoice_id}`)}>{formatCurrency(inv.total)}</td>
                  <td className="px-4 py-3 text-right text-green-600 hidden md:table-cell" onClick={() => navigate(`/invoices/${inv.invoice_id}`)}>{formatCurrency(inv.amount_paid)}</td>
                  <td className="px-4 py-3 text-right font-medium" onClick={() => navigate(`/invoices/${inv.invoice_id}`)}>{formatCurrency(inv.balance)}</td>
                  <td className="px-4 py-3" onClick={() => navigate(`/invoices/${inv.invoice_id}`)}><Badge className={`rounded-full text-xs ${statusColors[inv.status] || ""}`}>{inv.status}</Badge></td>
                  <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-400 hover:text-blue-600" title="Download PDF"
                        onClick={() => handleBulkDownload([inv.invoice_id])}><Download className="w-4 h-4" /></Button>
                      {user?.role === "admin" && inv.status === "Draft" && (
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-400 hover:text-[#E63946]" onClick={() => setDeleteId(inv.invoice_id)}><Trash2 className="w-4 h-4" /></Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
            <span className="text-xs text-gray-500">Page {page} of {totalPages}</span>
            <div className="flex items-center gap-1">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} className="h-8 rounded-sm"><ChevronLeft className="w-4 h-4" /></Button>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)} className="h-8 rounded-sm"><ChevronRight className="w-4 h-4" /></Button>
            </div>
          </div>
        )}
      </Card>

      <InvoiceFormDialog open={dialogOpen} onOpenChange={setDialogOpen} onSuccess={() => { setDialogOpen(false); fetchInvoices(); }} />

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete Invoice?</AlertDialogTitle><AlertDialogDescription>This will permanently delete this invoice.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function InvoiceFormDialog({ open, onOpenChange, onSuccess, editData }) {
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [customerId, setCustomerId] = useState(editData?.customer_id || "");
  const [items, setItems] = useState(editData?.line_items || []);
  const [discountType, setDiscountType] = useState(editData?.discount_type || "percentage");
  const [discountValue, setDiscountValue] = useState(editData?.discount_value || 0);
  const [vatRate, setVatRate] = useState(editData?.vat_rate || 15);
  const [dueDate, setDueDate] = useState(editData?.due_date || "");
  const [paymentTerms, setPaymentTerms] = useState(editData?.payment_terms || "30 days");
  const [reference, setReference] = useState(editData?.reference || "");
  const [notes, setNotes] = useState(editData?.notes || "");
  const [terms, setTerms] = useState(editData?.terms || "");
  const [saving, setSaving] = useState(false);
  const [productSearch, setProductSearch] = useState("");
  const [overrideModalIdx, setOverrideModalIdx] = useState(null);
  const [overrideReason, setOverrideReason] = useState("");
  const [vatConfig, setVatConfig] = useState(null);

  const { priceListInfo, loadingPrices, fetchPrice, onQuantityChange, onPriceChange, hasUnresolvedOverrides, overrideCount } = usePriceListPricing(customerId, items, setItems);

  const calcDueDate = (terms) => {
    const d = new Date();
    const days = parseInt(terms) || 30;
    if (terms === "Cash on delivery") return d.toISOString().split("T")[0];
    d.setDate(d.getDate() + days);
    return d.toISOString().split("T")[0];
  };

  useEffect(() => {
    if (open) {
      api.get("/customers", { params: { limit: 200, status: "Active" } }).then((r) => setCustomers(r.data.customers || [])).catch(() => {});
      api.get("/products", { params: { limit: 200, status: "Active" } }).then((r) => setProducts(r.data.products || [])).catch(() => {});
      api.get("/vat/config").then((r) => setVatConfig(r.data)).catch(() => {});
      api.get("/settings").then((r) => {
        if (!editData) {
          setTerms(r.data.default_invoice_terms || "");
          setVatRate(r.data.default_vat_rate || 15);
          const pt = r.data.default_payment_terms || "30 days";
          setPaymentTerms(pt);
          setDueDate(calcDueDate(pt));
        }
      }).catch(() => {});
      if (!editData) {
        setCustomerId(""); setItems([]); setDiscountType("percentage"); setDiscountValue(0); setDueDate(calcDueDate("30 days")); setReference(""); setNotes("");
      }
    }
  }, [open, editData]);

  const handlePaymentTermsChange = (v) => {
    setPaymentTerms(v);
    setDueDate(calcDueDate(v));
  };

  const addProduct = async (product) => {
    let unitPrice = product.selling_price || 0;
    let priceSource = "";
    let listPrice = 0;
    if (customerId && product.product_id) {
      const priceData = await fetchPrice(product.product_id, 1);
      if (priceData && priceData.source !== "base") {
        unitPrice = priceData.price;
        priceSource = priceData.price_list || "";
        listPrice = priceData.price;
      }
    }
    setItems([...items, { item_id: `item_${Date.now()}`, product_id: product.product_id, sku: product.sku, description: product.product_name, quantity: 1, unit_price: unitPrice, discount_percent: 0, price_source: priceSource, list_price: listPrice, is_override: false, override_reason: "" }]);
    setProductSearch("");
  };
  const addCustomItem = () => setItems([...items, { item_id: `item_${Date.now()}`, product_id: "", sku: "", description: "", quantity: 1, unit_price: 0, discount_percent: 0, price_source: "", list_price: 0, is_override: false, override_reason: "" }]);
  const updateItem = (idx, field, value) => { const next = [...items]; next[idx] = { ...next[idx], [field]: value }; setItems(next); };
  const removeItem = (idx) => setItems(items.filter((_, i) => i !== idx));

  const calcLineTotal = (item) => { const sub = (parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0); return sub - (sub * (parseFloat(item.discount_percent) || 0) / 100); };
  const vatEnabled = vatConfig?.vat_enabled;
  const subtotal = items.reduce((sum, i) => sum + calcLineTotal(i), 0);
  const discAmt = discountType === "percentage" ? subtotal * (parseFloat(discountValue) || 0) / 100 : parseFloat(discountValue) || 0;
  const afterDisc = subtotal - discAmt;

  // VAT calculation: per-item when VAT enabled, flat rate otherwise
  let vatAmt, total, vatBreakdown = [];
  if (vatEnabled) {
    const groups = {};
    const stdRate = vatConfig?.standard_vat_rate || 15;
    items.forEach((item) => {
      const prod = products.find((p) => p.product_id === item.product_id);
      const code = prod?.vat_code || "standard";
      const rate = code === "standard" ? stdRate : 0;
      const lt = calcLineTotal(item);
      if (!groups[code]) groups[code] = { vat_code: code, vat_rate: rate, taxable_amount: 0, vat_amount: 0 };
      groups[code].taxable_amount += lt;
      groups[code].vat_amount += lt * rate / 100;
    });
    vatBreakdown = Object.values(groups).map((g) => ({ ...g, taxable_amount: Math.round(g.taxable_amount * 100) / 100, vat_amount: Math.round(g.vat_amount * 100) / 100 }));
    // Scale for doc-level discount
    if (discAmt > 0 && subtotal > 0) {
      const ratio = afterDisc / subtotal;
      vatBreakdown = vatBreakdown.map((g) => ({ ...g, taxable_amount: Math.round(g.taxable_amount * ratio * 100) / 100, vat_amount: Math.round(g.vat_amount * ratio * 100) / 100 }));
    }
    vatAmt = vatBreakdown.reduce((s, g) => s + g.vat_amount, 0);
    total = afterDisc + vatAmt;
  } else {
    vatAmt = afterDisc * (parseFloat(vatRate) || 0) / 100;
    total = afterDisc + vatAmt;
  }

  const filteredProducts = productSearch ? products.filter((p) => p.product_name?.toLowerCase().includes(productSearch.toLowerCase()) || p.sku?.toLowerCase().includes(productSearch.toLowerCase())).slice(0, 8) : [];

  const handleSave = async () => {
    if (!customerId) { toast.error("Please select a customer"); return; }
    if (items.length === 0) { toast.error("Add at least one line item"); return; }
    if (hasUnresolvedOverrides) { toast.error("Please provide a reason for all price overrides"); return; }
    setSaving(true);
    try {
      const payload = {
        customer_id: customerId, line_items: items, discount_type: discountType, discount_value: discountValue,
        vat_rate: vatRate, due_date: dueDate, payment_terms: paymentTerms, reference, notes, terms,
        price_list_id: priceListInfo?.price_list_id || "", price_list_name: priceListInfo?.price_list_name || "",
        vat_breakdown: vatBreakdown, subtotal_excl_vat: afterDisc, total_vat: vatAmt, total_incl_vat: total,
      };
      if (editData) { await api.put(`/invoices/${editData.invoice_id}`, payload); toast.success("Invoice updated"); }
      else { await api.post("/invoices", payload); toast.success("Invoice created"); }
      onSuccess();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save invoice"); }
    finally { setSaving(false); }
  };

  const openOverrideModal = (idx) => { setOverrideModalIdx(idx); setOverrideReason(items[idx]?.override_reason || ""); };
  const confirmOverride = () => {
    if (overrideModalIdx !== null) {
      updateItem(overrideModalIdx, "override_reason", overrideReason);
      setOverrideModalIdx(null); setOverrideReason("");
    }
  };
  const cancelOverride = () => {
    if (overrideModalIdx !== null) {
      const item = items[overrideModalIdx];
      if (item.list_price) {
        const next = [...items]; next[overrideModalIdx] = { ...item, unit_price: item.list_price, is_override: false, override_reason: "" }; setItems(next);
      }
    }
    setOverrideModalIdx(null); setOverrideReason("");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading font-semibold text-xl">{editData ? "Edit Invoice" : "Create New Invoice"}</DialogTitle></DialogHeader>
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#374151] mb-1">Customer <span className="text-[#E63946]">*</span></label>
              <Select value={customerId} onValueChange={setCustomerId}>
                <SelectTrigger data-testid="invoice-customer-select" className="rounded-sm border-gray-300 h-10"><SelectValue placeholder="Select customer" /></SelectTrigger>
                <SelectContent>{customers.map((c) => <SelectItem key={c.customer_id} value={c.customer_id}>{c.company_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#374151] mb-1">Payment Terms</label>
              <Select value={paymentTerms} onValueChange={handlePaymentTermsChange}>
                <SelectTrigger className="rounded-sm border-gray-300 h-10"><SelectValue /></SelectTrigger>
                <SelectContent>{["Cash on delivery", "7 days", "14 days", "30 days", "60 days", "90 days"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#374151] mb-1">Due Date</label>
              <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="rounded-sm border-gray-300 h-10" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#374151] mb-1">Reference</label>
              <Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="PO / Ref #" className="rounded-sm border-gray-300 h-10" />
            </div>
          </div>

          {/* Price List Indicator Banner */}
          {priceListInfo && (
            <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-sm px-3 py-2" data-testid="price-list-banner">
              <Tag className="w-4 h-4 text-blue-600 shrink-0" />
              <p className="text-sm text-blue-800 flex-1">
                Using Price List: <span className="font-semibold">{priceListInfo.price_list_name}</span>
                {priceListInfo.is_system_default && <span className="text-blue-500 ml-1">(System Default)</span>}
                <span className="text-blue-400 ml-2">({priceListInfo.type} &middot; {priceListInfo.product_count} products)</span>
              </p>
              {loadingPrices && <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />}
            </div>
          )}

          {/* Override Summary Warning */}
          {overrideCount > 0 && (
            <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-sm px-3 py-2" data-testid="override-warning-banner">
              <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
              <p className="text-sm text-amber-800">
                {overrideCount} price {overrideCount === 1 ? "override" : "overrides"} detected.
                {hasUnresolvedOverrides && <span className="font-semibold text-amber-900 ml-1">Reasons required before saving.</span>}
              </p>
            </div>
          )}

          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-medium text-[#374151]">Line Items <span className="text-[#E63946]">*</span></label>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Input value={productSearch} onChange={(e) => setProductSearch(e.target.value)} placeholder="Search products..." className="rounded-sm border-gray-300 h-8 w-48 text-xs" />
                  {filteredProducts.length > 0 && (
                    <div className="absolute z-50 top-full left-0 w-72 bg-white border border-gray-200 rounded-sm shadow-lg mt-1 max-h-48 overflow-y-auto">
                      {filteredProducts.map((p) => (
                        <button key={p.product_id} onClick={() => addProduct(p)} className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center justify-between border-b border-gray-100 last:border-0">
                          <span className="truncate">{p.product_name}</span>
                          <span className="text-xs text-gray-400 ml-2 shrink-0">{p.sku} - {formatCurrency(p.selling_price)}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <Button type="button" variant="outline" size="sm" onClick={addCustomItem} className="rounded-sm text-xs h-8"><Plus className="w-3 h-3 mr-1" /> Custom Item</Button>
              </div>
            </div>
            {items.length === 0 ? (
              <div className="border-2 border-dashed border-gray-200 rounded-sm p-8 text-center">
                <Package className="w-8 h-8 mx-auto mb-2 text-gray-300" /><p className="text-sm text-gray-400">Search for products or add custom items</p>
              </div>
            ) : (
              <div className="border border-gray-200 rounded-sm overflow-hidden">
                <table className="w-full text-sm">
                  <thead><tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500">Description</th>
                    <th className="text-right px-3 py-2 text-xs font-semibold text-gray-500 w-20">Qty</th>
                    <th className="text-right px-3 py-2 text-xs font-semibold text-gray-500 w-32">Unit Price</th>
                    <th className="text-right px-3 py-2 text-xs font-semibold text-gray-500 w-20">Disc%</th>
                    <th className="text-right px-3 py-2 text-xs font-semibold text-gray-500 w-28">Total</th>
                    <th className="w-10"></th>
                  </tr></thead>
                  <tbody>
                    {items.map((item, idx) => {
                      const listP = parseFloat(item.list_price) || 0;
                      const curP = parseFloat(item.unit_price) || 0;
                      const diff = listP > 0 ? curP - listP : 0;
                      const pctDiff = listP > 0 ? ((diff / listP) * 100).toFixed(1) : 0;
                      return (
                        <tr key={item.item_id} className={`border-b border-gray-100 ${item.is_override ? "bg-amber-50/50" : ""}`}>
                          <td className="px-2 py-1.5"><Input value={item.description} onChange={(e) => updateItem(idx, "description", e.target.value)} placeholder="Description" className="rounded-sm border-gray-200 h-8 text-sm" /></td>
                          <td className="px-2 py-1.5"><Input type="number" value={item.quantity}
                            onChange={(e) => item.product_id ? onQuantityChange(idx, e.target.value) : updateItem(idx, "quantity", e.target.value)}
                            className="rounded-sm border-gray-200 h-8 text-sm text-right" /></td>
                          <td className="px-2 py-1.5">
                            <Input type="number" step="0.01" value={item.unit_price}
                              onChange={(e) => item.list_price > 0 ? onPriceChange(idx, e.target.value) : updateItem(idx, "unit_price", e.target.value)}
                              className={`rounded-sm h-8 text-sm text-right ${item.is_override ? "border-amber-400 bg-amber-50" : "border-gray-200"}`} />
                            {item.price_source && !item.is_override && (
                              <p className="text-[10px] text-blue-500 text-right mt-0.5" data-testid={`price-source-${idx}`}>{item.price_source}</p>
                            )}
                            {item.is_override && (
                              <div className="text-right mt-0.5">
                                <span className={`text-[10px] font-semibold ${diff > 0 ? "text-red-500" : "text-green-600"}`} data-testid={`override-indicator-${idx}`}>
                                  {diff > 0 ? "+" : ""}{formatCurrency(diff)} ({diff > 0 ? "+" : ""}{pctDiff}%)
                                </span>
                                <button onClick={() => openOverrideModal(idx)} className="text-[10px] text-amber-600 hover:text-amber-800 ml-1.5 underline" data-testid={`override-reason-btn-${idx}`}>
                                  {item.override_reason ? "Edit reason" : "Add reason"}
                                </button>
                              </div>
                            )}
                          </td>
                          <td className="px-2 py-1.5"><Input type="number" step="0.1" value={item.discount_percent} onChange={(e) => updateItem(idx, "discount_percent", e.target.value)} className="rounded-sm border-gray-200 h-8 text-sm text-right" /></td>
                          <td className="px-3 py-1.5 text-right font-medium">{formatCurrency(calcLineTotal(item))}</td>
                          <td className="px-1 py-1.5"><Button variant="ghost" size="icon" className="h-7 w-7 text-gray-400 hover:text-[#E63946]" onClick={() => removeItem(idx)}><X className="w-3.5 h-3.5" /></Button></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {items.length > 0 && (
            <div className="flex justify-end">
              <div className="w-80 space-y-2">
                <div className="flex justify-between text-sm"><span className="text-gray-500">Subtotal</span><span>{formatCurrency(subtotal)}</span></div>
                <div className="flex items-center justify-between text-sm gap-2">
                  <span className="text-gray-500">Discount</span>
                  <div className="flex items-center gap-1">
                    <Select value={discountType} onValueChange={setDiscountType}><SelectTrigger className="h-7 w-16 rounded-sm border-gray-200 text-xs"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="percentage">%</SelectItem><SelectItem value="fixed">R</SelectItem></SelectContent></Select>
                    <Input type="number" value={discountValue} onChange={(e) => setDiscountValue(e.target.value)} className="h-7 w-16 rounded-sm border-gray-200 text-xs text-right" />
                  </div>
                  <span className="text-[#E63946]">-{formatCurrency(discAmt)}</span>
                </div>
                {vatEnabled && vatBreakdown.length > 0 ? (
                  <>
                    <div className="border-t border-gray-100 pt-1.5">
                      <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-1">VAT Breakdown</p>
                      {vatBreakdown.map((g, i) => (
                        <div key={i} className="flex justify-between text-xs text-gray-600">
                          <span>{g.vat_code === "standard" ? "Standard" : g.vat_code === "zero-rated" ? "Zero-Rated" : g.vat_code === "exempt" ? "Exempt" : g.vat_code} ({g.vat_rate}%)</span>
                          <span>{formatCurrency(g.vat_amount)}</span>
                        </div>
                      ))}
                    </div>
                    <div className="flex justify-between text-sm"><span className="text-gray-500 font-medium">Total VAT</span><span>{formatCurrency(vatAmt)}</span></div>
                  </>
                ) : (
                  <div className="flex items-center justify-between text-sm gap-2">
                    <span className="text-gray-500">VAT</span>
                    <Input type="number" value={vatRate} onChange={(e) => setVatRate(e.target.value)} className="h-7 w-16 rounded-sm border-gray-200 text-xs text-right" />
                    <span>{formatCurrency(vatAmt)}</span>
                  </div>
                )}
                <div className="flex justify-between font-bold text-lg border-t border-gray-200 pt-2"><span>Total</span><span className="text-[#E63946]">{formatCurrency(total)}</span></div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium text-[#374151] mb-1">Notes</label>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes..." className="w-full rounded-sm border border-gray-300 px-3 py-2 text-sm h-20 resize-none focus:outline-none focus:ring-2 focus:ring-[#E63946] focus:ring-offset-1" /></div>
            <div><label className="block text-sm font-medium text-[#374151] mb-1">Terms & Conditions</label>
              <textarea value={terms} onChange={(e) => setTerms(e.target.value)} placeholder="Terms..." className="w-full rounded-sm border border-gray-300 px-3 py-2 text-sm h-20 resize-none focus:outline-none focus:ring-2 focus:ring-[#E63946] focus:ring-offset-1" /></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Cancel</Button>
          <Button data-testid="save-invoice-button" onClick={handleSave} disabled={saving || hasUnresolvedOverrides} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm">
            {saving ? "Saving..." : editData ? "Update Invoice" : "Create Invoice"}
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* Override Reason Modal */}
      <Dialog open={overrideModalIdx !== null} onOpenChange={(o) => { if (!o) cancelOverride(); }}>
        <DialogContent className="max-w-md" data-testid="override-reason-modal">
          <DialogHeader><DialogTitle className="text-lg font-semibold">Price Override Reason</DialogTitle></DialogHeader>
          {overrideModalIdx !== null && items[overrideModalIdx] && (() => {
            const item = items[overrideModalIdx];
            const lp = parseFloat(item.list_price) || 0;
            const cp = parseFloat(item.unit_price) || 0;
            const d = cp - lp;
            return (
              <div className="space-y-4">
                <div className="bg-gray-50 rounded-sm p-3 space-y-1.5 text-sm">
                  <p><span className="text-gray-500">Product:</span> <span className="font-medium">{item.description}</span></p>
                  <div className="grid grid-cols-2 gap-2">
                    <p><span className="text-gray-500">List Price:</span> <span className="font-medium">{formatCurrency(lp)}</span></p>
                    <p><span className="text-gray-500">Override Price:</span> <span className={`font-bold ${d > 0 ? "text-red-600" : "text-green-600"}`}>{formatCurrency(cp)}</span></p>
                  </div>
                  <p><span className="text-gray-500">Difference:</span> <span className={`font-semibold ${d > 0 ? "text-red-600" : "text-green-600"}`}>{d > 0 ? "+" : ""}{formatCurrency(d)} ({lp > 0 ? ((d / lp) * 100).toFixed(1) : 0}%)</span></p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#374151] mb-1">Reason for Override <span className="text-[#E63946]">*</span></label>
                  <textarea value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)} placeholder="e.g., Bulk deal negotiation, Special promotion, Customer loyalty discount..."
                    className="w-full rounded-sm border border-gray-300 px-3 py-2 text-sm h-20 resize-none focus:outline-none focus:ring-2 focus:ring-[#E63946] focus:ring-offset-1" data-testid="override-reason-input" />
                  <p className="text-xs text-gray-400 mt-1">This will be tracked for audit purposes</p>
                </div>
              </div>
            );
          })()}
          <DialogFooter>
            <Button variant="outline" onClick={cancelOverride} className="rounded-sm">Revert to List Price</Button>
            <Button onClick={confirmOverride} disabled={!overrideReason.trim()} className="bg-amber-600 hover:bg-amber-700 text-white rounded-sm" data-testid="confirm-override-btn">Confirm Override</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}

function InvoiceDetail({ invoiceId }) {
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [voidOpen, setVoidOpen] = useState(false);
  const [voidReason, setVoidReason] = useState("");
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [editPaymentId, setEditPaymentId] = useState(null);
  const [deletePaymentId, setDeletePaymentId] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const canEdit = ["admin", "sales_manager"].includes(user?.role);

  const fetchInvoice = useCallback(async () => {
    try { const res = await api.get(`/invoices/${invoiceId}`); setInvoice(res.data); }
    catch { toast.error("Invoice not found"); }
    finally { setLoading(false); }
  }, [invoiceId]);

  useEffect(() => { fetchInvoice(); }, [fetchInvoice]);

  const handleAction = async (action, body = {}) => {
    try {
      if (action === "send") await api.post(`/invoices/${invoiceId}/send`);
      else if (action === "void") await api.post(`/invoices/${invoiceId}/void`, body);
      else if (action === "duplicate") { await api.post(`/invoices/${invoiceId}/duplicate`); navigate("/invoices"); return; }
      toast.success(`Invoice ${action === "send" ? "sent" : action === "void" ? "voided" : "duplicated"}`);
      fetchInvoice();
    } catch (err) { toast.error(err.response?.data?.detail || `Failed to ${action}`); }
  };

  const handleDeleteInvoice = async () => {
    try { await api.delete(`/invoices/${invoiceId}`); toast.success("Invoice deleted"); navigate("/invoices"); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed to delete"); }
  };

  const handleDeletePayment = async () => {
    try { await api.delete(`/payments/${deletePaymentId}`); toast.success("Payment deleted"); setDeletePaymentId(null); fetchInvoice(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed to delete payment"); }
  };

  const handleDownloadPdf = async () => {
    setPdfLoading(true);
    try {
      const res = await api.get(`/invoices/${invoiceId}/pdf`, { responseType: "blob" });
      triggerDownload(new Blob([res.data]), `Invoice_${invoice.invoice_number}.pdf`);
      toast.success("Invoice PDF downloaded");
    } catch { toast.error("Failed to generate PDF"); }
    finally { setPdfLoading(false); }
  };

  if (loading) return <div className="animate-pulse h-64 bg-gray-100 rounded-sm" />;
  if (!invoice) return <p className="text-gray-400">Invoice not found</p>;

  const isOverdue = invoice.status !== "Paid" && invoice.status !== "Void" && invoice.due_date && invoice.due_date < new Date().toISOString().split("T")[0];
  const daysOverdue = isOverdue ? Math.floor((Date.now() - new Date(invoice.due_date).getTime()) / 86400000) : 0;

  return (
    <div className="animate-fade-in" data-testid="invoice-detail">
      {isOverdue && (
        <div className="bg-red-50 border border-red-200 rounded-sm p-3 mb-4 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-red-600 shrink-0" />
          <span className="text-sm font-medium text-red-700">OVERDUE - {daysOverdue} days past due date</span>
        </div>
      )}

      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate("/invoices")} className="h-8 w-8"><ArrowLeft className="w-4 h-4" /></Button>
        <div className="flex-1">
          <h1 className="font-heading font-bold text-2xl text-[#1F2937]">{invoice.invoice_number}</h1>
          <div className="flex items-center gap-2 mt-1">
            <Badge className={`rounded-full text-xs ${statusColors[invoice.status] || ""}`}>{invoice.status}</Badge>
            {invoice.email_sent && <Badge className="rounded-full text-xs bg-green-50 text-green-700 border border-green-200"><Mail className="w-3 h-3 mr-1 inline" />Emailed</Badge>}
            <span className="text-sm text-gray-500">{invoice.customer_name}</span>
            {invoice.quote_number && <span className="text-xs text-gray-400">from {invoice.quote_number}</span>}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button data-testid="download-invoice-pdf" variant="outline" size="sm" onClick={handleDownloadPdf} disabled={pdfLoading} className="rounded-sm">
            {pdfLoading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1" />} {pdfLoading ? "Generating..." : "Download PDF"}
          </Button>
          <Button data-testid="download-tax-invoice-pdf" variant="outline" size="sm" onClick={async () => {
            try {
              const res = await api.get(`/invoices/${invoice.invoice_id}/tax-invoice-pdf`, { responseType: "blob" });
              triggerDownload(new Blob([res.data], { type: "application/pdf" }), `TaxInvoice_${invoice.invoice_number}.pdf`);
            } catch { toast.error("Failed to generate tax invoice PDF"); }
          }} className="rounded-sm text-[#E63946] border-[#E63946]/30 hover:bg-red-50">
            <FileText className="w-3.5 h-3.5 mr-1" /> Tax Invoice PDF
          </Button>
          {invoice.status === "Draft" && canEdit && (
            <>
              <Button variant="outline" size="sm" onClick={() => setEditOpen(true)} className="rounded-sm"><Pencil className="w-3.5 h-3.5 mr-1" /> Edit</Button>
              <Button size="sm" onClick={() => handleAction("send")} className="bg-blue-600 hover:bg-blue-700 text-white rounded-sm"><Send className="w-3.5 h-3.5 mr-1" /> Send</Button>
            </>
          )}
          {invoice.email_sent && invoice.status !== "Draft" && canEdit && (
            <Button variant="outline" size="sm" onClick={async () => { try { await api.post("/email/resend", { type: "invoice", id: invoice.invoice_id }); toast.success("Email resent successfully"); } catch { toast.error("Failed to resend email"); }}} className="rounded-sm text-blue-600 border-blue-200 hover:bg-blue-50"><Mail className="w-3.5 h-3.5 mr-1" /> Resend Email</Button>
          )}
          {["Sent", "Partially Paid", "Overdue"].includes(invoice.status) && canEdit && (
            <Button data-testid="record-payment-button" size="sm" onClick={() => { setEditPaymentId(null); setPaymentOpen(true); }} className="bg-green-600 hover:bg-green-700 text-white rounded-sm">
              <DollarSign className="w-3.5 h-3.5 mr-1" /> Record Payment
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => handleAction("duplicate")} className="rounded-sm"><Copy className="w-3.5 h-3.5 mr-1" /> Duplicate</Button>
          {invoice.status !== "Void" && isAdmin && (
            <Button variant="outline" size="sm" onClick={() => setVoidOpen(true)} className="rounded-sm text-red-600 border-red-200 hover:bg-red-50"><Ban className="w-3.5 h-3.5 mr-1" /> Void</Button>
          )}
          {invoice.status === "Draft" && isAdmin && (
            <Button variant="outline" size="sm" onClick={() => setDeleteOpen(true)} className="rounded-sm text-red-600 border-red-200 hover:bg-red-50"><Trash2 className="w-3.5 h-3.5 mr-1" /> Delete</Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card className="border-2 border-gray-200 rounded-sm shadow-sm">
            <CardContent className="p-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div><p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Total Amount</p><p className="font-heading font-bold text-xl text-[#1F2937]">{formatCurrency(invoice.total)}</p></div>
                <div><p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Amount Paid</p><p className="font-heading font-bold text-xl text-green-600">{formatCurrency(invoice.amount_paid)}</p></div>
                <div><p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Outstanding</p><p className={`font-heading font-bold text-xl ${invoice.balance > 0 ? "text-[#E63946]" : "text-green-600"}`}>{formatCurrency(invoice.balance)}</p></div>
                <div><p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Due Date</p><p className={`font-heading font-bold text-sm ${isOverdue ? "text-[#E63946]" : "text-[#1F2937]"}`}>{invoice.due_date || "-"}</p></div>
              </div>
            </CardContent>
          </Card>

          <Card className="border border-gray-200 rounded-sm shadow-sm">
            <CardContent className="p-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div><p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Customer</p><p className="text-sm font-medium">{invoice.customer_name}</p></div>
                <div><p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Created</p><p className="text-sm">{formatDateLong(invoice.created_at)}</p></div>
                <div><p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Payment Terms</p><p className="text-sm">{invoice.payment_terms || "-"}</p></div>
                <div><p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Reference</p><p className="text-sm">{invoice.reference || "-"}</p></div>
              </div>
            </CardContent>
          </Card>

          <Card className="border border-gray-200 rounded-sm shadow-sm">
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead><tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Description</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500">Qty</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500">Unit Price</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500">Disc%</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500">Total</th>
                </tr></thead>
                <tbody>
                  {(invoice.line_items || []).map((item) => (
                    <tr key={item.item_id} className="border-b border-gray-100">
                      <td className="px-4 py-3"><span className="font-medium">{item.description}</span>{item.sku && <span className="text-xs text-gray-400 ml-2">({item.sku})</span>}</td>
                      <td className="px-4 py-3 text-right">{item.quantity}</td>
                      <td className="px-4 py-3 text-right">{formatCurrency(item.unit_price)}</td>
                      <td className="px-4 py-3 text-right">{item.discount_percent > 0 ? `${item.discount_percent}%` : "-"}</td>
                      <td className="px-4 py-3 text-right font-medium">{formatCurrency(item.line_total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="p-4 border-t border-gray-200 flex justify-end">
                <div className="w-72 space-y-1.5">
                  <div className="flex justify-between text-sm"><span className="text-gray-500">Subtotal</span><span>{formatCurrency(invoice.subtotal)}</span></div>
                  {invoice.discount_amount > 0 && <div className="flex justify-between text-sm"><span className="text-gray-500">Discount</span><span className="text-[#E63946]">-{formatCurrency(invoice.discount_amount)}</span></div>}
                  {invoice.vat_breakdown?.length > 0 ? (
                    <>
                      <div className="border-t border-gray-100 pt-1">
                        <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-1">VAT Breakdown</p>
                        {invoice.vat_breakdown.map((g, i) => (
                          <div key={i} className="flex justify-between text-xs text-gray-600">
                            <span>{g.vat_code === "standard" ? "Standard" : g.vat_code === "zero-rated" ? "Zero-Rated" : g.vat_code === "exempt" ? "Exempt" : g.vat_code} ({g.vat_rate}%)</span>
                            <span>{formatCurrency(g.vat_amount)}</span>
                          </div>
                        ))}
                      </div>
                      <div className="flex justify-between text-sm"><span className="text-gray-500">Total VAT</span><span>{formatCurrency(invoice.total_vat || invoice.vat_amount)}</span></div>
                    </>
                  ) : (
                    <div className="flex justify-between text-sm"><span className="text-gray-500">VAT ({invoice.vat_rate}%)</span><span>{formatCurrency(invoice.vat_amount)}</span></div>
                  )}
                  {invoice.customer_vat_number && <div className="text-[10px] text-gray-400">Customer VAT: {invoice.customer_vat_number}</div>}
                  <div className="flex justify-between font-bold text-lg border-t border-gray-200 pt-2"><span>Total</span><span className="text-[#E63946]">{formatCurrency(invoice.total)}</span></div>
                </div>
              </div>
            </CardContent>
          </Card>

          {(invoice.payments || []).length > 0 && (
            <Card className="border border-gray-200 rounded-sm shadow-sm">
              <CardContent className="p-0">
                <div className="px-4 py-3 border-b border-gray-200 flex items-center gap-2">
                  <CreditCard className="w-4 h-4 text-[#E63946]" /><h3 className="text-sm font-semibold text-[#1F2937]">Payment History</h3>
                </div>
                <table className="w-full text-sm">
                  <thead><tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500">Date</th>
                    <th className="text-right px-4 py-2 text-xs font-semibold text-gray-500">Amount</th>
                    <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500">Method</th>
                    <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500">Reference</th>
                    <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500">Recorded By</th>
                    {isAdmin && <th className="text-right px-4 py-2 text-xs font-semibold text-gray-500">Actions</th>}
                  </tr></thead>
                  <tbody>
                    {invoice.payments.map((p) => (
                      <tr key={p.payment_id} className="border-b border-gray-100">
                        <td className="px-4 py-2.5">{p.payment_date}</td>
                        <td className="px-4 py-2.5 text-right font-medium text-green-600">{formatCurrency(p.amount)}</td>
                        <td className="px-4 py-2.5">{p.payment_method}</td>
                        <td className="px-4 py-2.5 text-gray-500">{p.reference || "-"}</td>
                        <td className="px-4 py-2.5 text-gray-500">{p.recorded_by_name}</td>
                        {isAdmin && (
                          <td className="px-4 py-2.5 text-right">
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-gray-400 hover:text-blue-600" onClick={() => { setEditPaymentId(p.payment_id); setPaymentOpen(true); }}><Pencil className="w-3.5 h-3.5" /></Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-gray-400 hover:text-[#E63946]" onClick={() => setDeletePaymentId(p.payment_id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}

          {(invoice.notes || invoice.terms) && (
            <Card className="border border-gray-200 rounded-sm shadow-sm"><CardContent className="p-6">
              {invoice.notes && <div className="mb-4"><p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Notes</p><p className="text-sm whitespace-pre-wrap">{invoice.notes}</p></div>}
              {invoice.terms && <div><p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Terms & Conditions</p><p className="text-sm whitespace-pre-wrap">{invoice.terms}</p></div>}
            </CardContent></Card>
          )}

          {invoice.status === "Void" && (
            <Card className="border border-red-200 rounded-sm shadow-sm bg-red-50"><CardContent className="p-6">
              <p className="text-xs text-red-500 uppercase tracking-wider mb-1">Void Reason</p><p className="text-sm text-red-700">{invoice.void_reason}</p>
            </CardContent></Card>
          )}
        </div>

        <div>
          <Card className="border border-gray-200 rounded-sm shadow-sm">
            <CardContent className="p-4">
              <h3 className="text-sm font-semibold text-[#1F2937] mb-3">Activity</h3>
              <div className="space-y-3">
                {(invoice.audit_trail || []).map((entry, i) => (
                  <div key={i} className="flex gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#E63946] mt-1.5 shrink-0" />
                    <div><p className="text-xs text-gray-700">{entry.details}</p><p className="text-xs text-gray-400">{entry.user_name} - {formatDateLong(entry.created_at)}</p></div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <PaymentDialog open={paymentOpen} onOpenChange={setPaymentOpen} invoice={invoice} editPaymentId={editPaymentId} onSuccess={() => { setPaymentOpen(false); setEditPaymentId(null); fetchInvoice(); }} />
      <InvoiceFormDialog open={editOpen} onOpenChange={setEditOpen} editData={invoice} onSuccess={() => { setEditOpen(false); fetchInvoice(); }} />

      <AlertDialog open={voidOpen} onOpenChange={setVoidOpen}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Void Invoice?</AlertDialogTitle>
            <AlertDialogDescription>
              This action is irreversible. The invoice will be marked as void.
              <textarea value={voidReason} onChange={(e) => setVoidReason(e.target.value)} placeholder="Reason for voiding (required)..."
                className="w-full rounded-sm border border-gray-300 px-3 py-2 text-sm h-20 resize-none mt-3 focus:outline-none focus:ring-2 focus:ring-[#E63946]" />
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => { handleAction("void", { reason: voidReason }); setVoidOpen(false); }} disabled={!voidReason.trim()} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm">Void Invoice</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete Invoice?</AlertDialogTitle><AlertDialogDescription>This will permanently delete {invoice.invoice_number}.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel><AlertDialogAction onClick={handleDeleteInvoice} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm">Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deletePaymentId} onOpenChange={() => setDeletePaymentId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete Payment?</AlertDialogTitle><AlertDialogDescription>This will remove the payment and recalculate the invoice balance.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel><AlertDialogAction onClick={handleDeletePayment} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm">Delete Payment</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function PaymentDialog({ open, onOpenChange, invoice, editPaymentId, onSuccess }) {
  const [amount, setAmount] = useState("");
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split("T")[0]);
  const [paymentMethod, setPaymentMethod] = useState("");
  const [reference, setReference] = useState("");
  const [bankStatementDate, setBankStatementDate] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && editPaymentId && invoice?.payments) {
      const p = invoice.payments.find((x) => x.payment_id === editPaymentId);
      if (p) { setAmount(p.amount || ""); setPaymentDate(p.payment_date || ""); setPaymentMethod(p.payment_method || ""); setReference(p.reference || ""); setBankStatementDate(p.bank_statement_date || ""); setNotes(p.notes || ""); }
    } else if (open) {
      setAmount(""); setPaymentDate(new Date().toISOString().split("T")[0]); setPaymentMethod(""); setReference(""); setBankStatementDate(""); setNotes("");
    }
  }, [open, editPaymentId, invoice]);

  const balance = parseFloat(invoice?.balance || 0);
  const maxAmount = editPaymentId ? balance + parseFloat(invoice?.payments?.find((p) => p.payment_id === editPaymentId)?.amount || 0) : balance;
  const amtNum = parseFloat(amount) || 0;
  const isPartial = amtNum > 0 && amtNum < balance && !editPaymentId;

  const handleSave = async () => {
    if (!amount || parseFloat(amount) <= 0) { toast.error("Payment amount must be greater than zero"); return; }
    if (!paymentMethod) { toast.error("Payment method is required"); return; }
    if (amtNum > maxAmount + 0.01) { toast.error(`Amount cannot exceed R${maxAmount.toFixed(2)}`); return; }
    setSaving(true);
    try {
      const payload = { amount: parseFloat(amount), payment_date: paymentDate, payment_method: paymentMethod, reference, bank_statement_date: bankStatementDate, notes };
      if (editPaymentId) { await api.put(`/payments/${editPaymentId}`, payload); toast.success("Payment updated"); }
      else {
        await api.post(`/invoices/${invoice.invoice_id}/payments`, payload);
        const remaining = balance - amtNum;
        toast.success(`Payment of ${formatCurrency(amtNum)} recorded. Outstanding balance: ${formatCurrency(Math.max(0, remaining))}`);
      }
      onSuccess();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save payment"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="font-heading font-semibold text-xl">{editPaymentId ? "Edit Payment" : "Record Payment"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          {!editPaymentId && (
            <div className="bg-gray-50 rounded-sm p-3 border border-gray-200">
              <div className="flex justify-between text-sm"><span className="text-gray-500">Invoice Total</span><span className="font-medium">{formatCurrency(invoice?.total)}</span></div>
              <div className="flex justify-between text-sm"><span className="text-gray-500">Already Paid</span><span className="text-green-600">{formatCurrency(invoice?.amount_paid)}</span></div>
              <div className="flex justify-between text-sm font-medium border-t border-gray-200 pt-1 mt-1"><span>Outstanding</span><span className="text-[#E63946]">{formatCurrency(balance)}</span></div>
            </div>
          )}
          <div><label className="block text-sm font-medium text-[#374151] mb-1">Payment Date <span className="text-[#E63946]">*</span></label>
            <Input data-testid="payment-date" type="date" value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)} className="rounded-sm border-gray-300 h-10" /></div>
          <div><label className="block text-sm font-medium text-[#374151] mb-1">Amount Received (R) <span className="text-[#E63946]">*</span></label>
            <Input data-testid="payment-amount" type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" className="rounded-sm border-gray-300 h-10" />
            {isPartial && <p className="text-xs text-orange-600 mt-1">This is a partial payment. Outstanding balance will be {formatCurrency(balance - amtNum)}</p>}</div>
          <div><label className="block text-sm font-medium text-[#374151] mb-1">Payment Method <span className="text-[#E63946]">*</span></label>
            <Select value={paymentMethod} onValueChange={setPaymentMethod}><SelectTrigger data-testid="payment-method" className="rounded-sm border-gray-300 h-10"><SelectValue placeholder="Select method" /></SelectTrigger>
              <SelectContent>{PAYMENT_METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent></Select></div>
          <div><label className="block text-sm font-medium text-[#374151] mb-1">Payment Reference</label>
            <Input data-testid="payment-reference" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Transaction reference" className="rounded-sm border-gray-300 h-10" /></div>
          <div><label className="block text-sm font-medium text-[#374151] mb-1">Bank Statement Date</label>
            <Input type="date" value={bankStatementDate} onChange={(e) => setBankStatementDate(e.target.value)} className="rounded-sm border-gray-300 h-10" /></div>
          <div><label className="block text-sm font-medium text-[#374151] mb-1">Notes</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Internal notes..."
              className="w-full rounded-sm border border-gray-300 px-3 py-2 text-sm h-16 resize-none focus:outline-none focus:ring-2 focus:ring-[#E63946] focus:ring-offset-1" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Cancel</Button>
          <Button data-testid="save-payment-button" onClick={handleSave} disabled={saving} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm">
            {saving ? "Saving..." : editPaymentId ? "Update Payment" : "Record Payment"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
